import _ from 'lodash';
import React from 'react';
import styled, { ThemeContext } from 'styled-components';
import {
  TextInput, SPassword, Button, HelpBlock, InputContainer,
} from '../common/styles';
import Loading from '../../../shared/loading';
import Email from '../../shared/sign-up/email';
import Password from '../../shared/sign-up/password';
import { useForm } from '../../../shared/form-validator-hook';
import { usePostSignIn } from '../../../services/auth-service';
import oauthProviderDisplayName from '../../shared/sign-up/oauth-provider-label';
import { recordEvent } from '../../../services/events-service';
import { stashBrazeExternalIdForRedirect } from '../../../services/braze/braze-user-identity';

const Form = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: ${props => (props.theme.isMobile ? '92%' : '33vw')};
  &&&&& input {
    width: 100%;
    height: 40px;
    border-radius: 4px;
    border: 1px solid ${props => props.theme.secondaryColor30Transparent};
    background-color: ${props => props.theme.secondaryColorLight};
    font-family: ${props => props.theme.fontFamilyBody};
    font-size: 13px;
    line-height: 13px;
    font-weight: ${props => props.theme.fontWeightHeavy};
    text-align: left;
    color: ${props => props.theme.secondaryColor};
    padding-left: 20px;

    &:focus, &:not(:placeholder-shown) {
      color: ${props => props.theme.bodyColor};
      background-color: white;
    }

    &::placeholder {
      color: ${props => props.theme.secondaryColor};
      text-transform: capitalize;
    }

    &[data-error="show"] {
      border-color: ${props => props.theme.errorColor};
    }

    &:not([data-error="show"]) + ${HelpBlock} {
      display: none;
    }
  }
`;

const getSignInError = (res, err) => {
  if (_.get(res, 'status') !== 201 && _.get(err, 'response.status') === 401) {
    const errorData = _.get(err, 'response.data', {});
    return {
      message: errorData.error || null,
      provider: errorData.provider || null,
    };
  }
  return null;
};

const renderSignInError = signInError => {
  if (signInError.provider) {
    return (
      <>
        {'You have an "Email Only" account using'}
        {' '}
        {oauthProviderDisplayName(signInError.provider)}
        {' '}
        {'oAuth. Sign in using'}
        {' '}
        {oauthProviderDisplayName(signInError.provider)}
        {' '}
        {'oAuth to access your account.'}
      </>
    );
  }
  if (signInError.message?.includes('complete registration')) {
    return (
      <>
        {'You have an "Email Only" account and need to'}
        {' '}
        <a href="/sign_up">complete registration</a>
        {' '}
        {'before you can Sign In'}
      </>
    );
  }
  return signInError.message || signInError;
};

export default function SignInForm() {
  const { redirectTo } = React.useContext(ThemeContext);
  const loadingRef = React.useRef();
  const [useLazy, setUseLazy] = React.useState(true);
  const {
    values, useInput, errors, validateForm,
  } = useForm({ email: '', password: '' }, { lazy: useLazy });
  const [{ response, loading: signInLoading, error }, signIn] = usePostSignIn();
  const [signInError, setSignInError] = React.useState(null);

  const commonProps = {
    Field: TextInput,
    Err: HelpBlock,
    useInput,
    errors,
  };

  const submit = e => {
    e.preventDefault();
    if (validateForm()) {
      signIn(values);
    } else {
      setUseLazy(false);
    }
  };

  const createEvent = () => {
    recordEvent({
      event: 'sign_in_with_livewire',
      id: 0,
      statisticable: 'navigation',
      page: '/sign_in',
    });
  };

  React.useEffect(() => {
    if (signInLoading) {
      loadingRef.current.start();
    }
    const err = getSignInError(response, error);
    const storeUserInfo = data => {
      localStorage.setItem('currentUser', JSON.stringify({
        profileName: data.profile_name,
        canPostWires: data.can_post_wires,
      }));
    };
    if (response && !err) {
      storeUserInfo(response.data);
      stashBrazeExternalIdForRedirect(_.get(response, 'data.braze_external_id'));
      window.location.href = redirectTo;
    } else if (err) {
      setSignInError(err);
      loadingRef.current.stop();
    }
  }, [response, error, signInLoading, redirectTo]);

  React.useEffect(() => {
    setSignInError(null);
  }, [values]);

  return (
    <Form onSubmit={submit}>
      <InputContainer>
        <Email {...commonProps} focus />
      </InputContainer>
      <InputContainer>
        <Password
          {...commonProps}
          Field={SPassword}
          peekaboo
          validateComplexity={false}
        />
      </InputContainer>
      {signInError && (
        <HelpBlock>
          {renderSignInError(signInError)}
        </HelpBlock>
      )}
      <InputContainer>
        <Loading variant="button" ref={loadingRef}>
          <Button type="submit" onClick={createEvent}>sign in</Button>
        </Loading>
      </InputContainer>
    </Form>
  );
}
